import { FC, ReactNode, useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { CRS } from 'leaflet'
import { MapContainer, useMap, ZoomControl } from 'react-leaflet'
import { mapDataAtom } from '@/js/editor/state/mapAtoms.ts'
import { convertToLatLng } from '@/js/helper/convertToLatLng.ts'
import { getContrastColor } from '@/js/helper/color.ts'
import { LoadingScreen } from '@/js/editor/components/map/LoadingScreen.tsx'
import { MapLayer } from '@/js/editor/components/map/MapLayer.tsx'
import { LayerRegistryProvider } from '@/js/editor/components/map/LayerRegistryContext.tsx'
import { isMapLoadingAtom } from '@/js/editor/state/mapSessionAtoms.ts'

type MapBackgroundProps = {
    color: string | null
}

const MapBackground: FC<MapBackgroundProps> = ({ color }) => {
    const map = useMap()

    useEffect(() => {
        const container = map.getContainer()
        container.style.backgroundColor = color ?? ''

        return () => {
            container.style.backgroundColor = ''
        }
    }, [map, color])

    return null
}

type MapCanvasProps = {
    children: ReactNode
    className?: string
    zoomControlPosition?: 'topright' | 'bottomright'
    scrollWheelZoom?: boolean
}

export const MapCanvas: FC<MapCanvasProps> = ({
    children,
    className = 'h-full',
    zoomControlPosition = 'topright',
    scrollWheelZoom = true,
}) => {
    const { center, zoom, map_type, colors } = useAtomValue(mapDataAtom)
    const isMapLoading = useAtomValue(isMapLoadingAtom)

    useEffect(() => {
        if (!colors.primary) {
            return
        }

        document.documentElement.style.setProperty('--link-button-bg', colors.primary)
        document.documentElement.style.setProperty('--link-button-fg', getContrastColor(colors.primary))
    }, [colors.primary])

    if (isMapLoading) {
        return <LoadingScreen />
    }

    return (
        <LayerRegistryProvider>
            <MapContainer
                center={convertToLatLng(center ?? [0, 0])}
                zoom={zoom}
                zoomControl={false}
                scrollWheelZoom={scrollWheelZoom}
                className={className}
                crs={map_type === 'image' ? CRS.Simple : CRS.EPSG3857}
            >
                <MapBackground color={colors.background} />
                <ZoomControl position={zoomControlPosition} />
                <MapLayer mapType={map_type} />
                {children}
            </MapContainer>
        </LayerRegistryProvider>
    )
}
